import { useEffect, useState, useCallback } from "react";
import api from "../../utils/api";

export default function PostReplies({
  parentId,
  postId,
  currentUser,
}) {
  const [show, setShow] = useState(false);
  const [replies, setReplies] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);

  /* ================= LOAD REPLIES ================= */

  const loadReplies = useCallback(async () => {
    if (!postId || !parentId) return;

    try {
      setLoadingReplies(true);

      const res = await api.get("/api/comments", {
        params: { postId },
      });

      const filtered =
        (res.data?.data || []).filter(
          (c) => String(c.parentId) === String(parentId)
        );

      setReplies(filtered);
      setLoadedOnce(true);

    } catch (err) {
      console.error("Failed to load replies:", err);
      setReplies([]);
    } finally {
      setLoadingReplies(false);
    }
  }, [postId, parentId]);

  /* Load only first time when opened */
  useEffect(() => {
    if (show && !loadedOnce) {
      loadReplies();
    }
  }, [show, loadedOnce, loadReplies]);

  /* ================= HANDLE REPLY ================= */

  const handleReply = async () => {
    const trimmed = input.trim();
    if (!currentUser || !trimmed || loading) return;

    try {
      setLoading(true);

      const res = await api.post("/api/comments", {
        postId,
        parentId,
        text: trimmed,
      });

      const newReply = res.data?.data;

      if (newReply) {
        // Optimistic add without reload
        setReplies((prev) => [...prev, newReply]);
      } else {
        loadReplies();
      }

      setInput("");

    } catch (err) {
      console.error("Reply failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleReply();
    }
  };

  return (
    <div className="comment-replies-wrapper">

      {/* TOGGLE */}
      <div
        className="reply-toggle"
        onClick={() => setShow((prev) => !prev)}
      >
        {show
          ? "Hide replies"
          : `View replies (${replies.length})`}
      </div>

      {show && (
        <>
          {loadingReplies && (
            <div className="comment-loading">
              Loading replies...
            </div>
          )}

          {!loadingReplies &&
            replies.map((r) => (
              <div
                key={r._id}
                className="comment-item reply-item"
              >
                <img
                  src={
                    r.user?.avatar ||
                    `https://ui-avatars.com/api/?name=${encodeURIComponent(
                      r.user?.name || "User"
                    )}&background=1877f2&color=fff`
                  }
                  alt="avatar"
                  className="comment-avatar"
                />

                <div className="comment-content">
                  <div className="comment-name">
                    {r.user?.name || "User"}
                  </div>

                  <div className="comment-text">
                    {r.text}
                  </div>
                </div>
              </div>
            ))}

          {!loadingReplies && replies.length === 0 && (
            <div className="comment-empty">
              No replies yet
            </div>
          )}

          {currentUser && (
            <div className="comment-input-row reply-input">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Write a reply..."
                disabled={loading}
                rows={1}
                style={{ resize: "none" }}
              />

              <button
                onClick={handleReply}
                disabled={loading || !input.trim()}
              >
                {loading ? "Replying..." : "Reply"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
